import { QueryKey, queryOptions, skipToken } from '@tanstack/react-query'
import { Address } from 'viem'
import { avalanche, mainnet, optimism } from 'viem/chains'
import { Config, useChains } from 'wagmi'

import { fetchSparkRewards } from './fetchSparkRewards'
import { spark2ApiUrl } from '@/config/consts'
import { sparkRewardsConfig } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'

export interface ClaimableRewardsQueryOptionsParams {
  wagmiConfig: Config
  account?: Address
  isInSandbox: boolean
  sandboxChainId: number | undefined
  configuredChains: ReturnType<typeof useChains>
}

const SPARK_REWARDS_CHAIN_IDS = [
  mainnet.id,
  optimism.id,
  ...(import.meta.env.VITE_FEATURE_AVALANCHE_REWARDS_SUPPORT === '1' ? [avalanche.id] : []),
]

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function claimableRewardsQueryOptions({
  wagmiConfig,
  account,
  isInSandbox,
  sandboxChainId,
  configuredChains,
}: ClaimableRewardsQueryOptionsParams) {
  // connectedChainId is used to fetch sandbox mock rewards. Unique is used to remove duplicate if connected to supported chain.
  const sparkRewardChainIds = (
    isInSandbox ? [...SPARK_REWARDS_CHAIN_IDS, sandboxChainId].filter(Boolean) : SPARK_REWARDS_CHAIN_IDS
  ).filter((chainId) => configuredChains.some((chain) => chain.id === chainId))

  return queryOptions({
    queryKey: claimableRewardsQueryKey({ account }),
    queryFn: !account
      ? skipToken
      : async () =>
          (
            await Promise.all(
              sparkRewardChainIds.map((chainId) =>
                fetchSparkRewards({
                  chainId,
                  wagmiConfig,
                  account,
                  rewardsContractAddress: getContractAddress(sparkRewardsConfig.address, chainId),
                  createApiEndpoint: ({ chainId, merkleRoot, account }) =>
                    `${spark2ApiUrl}/rewards/roots/${chainId}/${merkleRoot}/${account}/`,
                }),
              ),
            )
          ).flat(),
  })
}

export function claimableRewardsQueryKey({ account }: { account?: Address }): QueryKey {
  return ['claimable-spark-rewards', account]
}
